/**
 * Buyer–Seller flow: Submit Quotation (Inspector Quotation) page.
 * Used only when inspector clicks "Submit Quotation" on a quotation-based QA request from QA dashboard.
 * Not shared with Auction flow; Auction flow uses InspectionRFQDetails / QARequestQuotationDetail.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import StorageService from '../../services/StorageService';
import { API_URLS, getApiOrigin } from '../../constants/api-urls';
import { useCurrencyMaster } from '../../hooks/useCurrencyMaster';
import './QuotationFlowInspectorQuotation.scss';

const FEE_PERCENT = 0.25;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const isNumericId = (val) => {
  if (val == null || val === '') return false;
  const n = Number(val);
  return !Number.isNaN(n) && Number.isInteger(n) && n > 0;
};

const FREQUENCY_OPTIONS = [
  { value: 'one-time', label: 'One-time' },
  { value: 'periodic', label: 'Periodic' },
  { value: 'ongoing', label: 'Ongoing' },
];
const SAMPLING_METHOD_OPTIONS = [
  { value: 'AQL 2.5 / 4.0', label: 'AQL 2.5 / 4.0' },
  { value: 'AQL 1.5 / 2.5', label: 'AQL 1.5 / 2.5' },
  { value: 'AQL 4.0 / 6.5', label: 'AQL 4.0 / 6.5' },
  { value: '100% Inspection', label: '100% Inspection' },
  { value: 'Custom', label: 'Custom' },
];
const BUDGET_RANGE_OPTIONS = [
  { value: '$1k-2.5k', label: '$1k-2.5k' },
  { value: '$2.5k-5k', label: '$2.5k-5k' },
  { value: '$5k-10k', label: '$5k-10k' },
  { value: '$10k-25k', label: '$10k-25k' },
  { value: '$25k+', label: '$25k+' },
];
const REPORT_FORMAT_OPTIONS = [
  { value: 'Platform Data', label: 'Platform Data' },
  { value: 'PDF Report', label: 'PDF Report' },
  { value: 'Excel Report', label: 'Excel Report' },
  { value: 'Custom Format', label: 'Custom Format' },
];
const MAX_TAT_OPTIONS = [
  { value: '24h', label: '24h' },
  { value: '48h', label: '48h' },
  { value: '72h', label: '72h' },
  { value: '1 week', label: '1 week' },
  { value: 'Custom', label: 'Custom' },
];

const resolveLabel = (options, value) => {
  if (value == null || value === '') return '';
  const opt = options.find((o) => String(o.value) === String(value));
  return opt ? opt.label : value;
};
const resolveLabels = (options, values) => {
  if (values == null) return '';
  let arr = values;
  if (typeof values === 'string') {
    try {
      const parsed = JSON.parse(values);
      arr = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      arr = [values];
    }
  } else if (!Array.isArray(values)) {
    arr = [values];
  }
  return arr.map((v) => resolveLabel(options, v)).filter(Boolean).join(', ') || '';
};

/** Parse fee breakdown from the first line of additional_notes (semicolon-separated). */
const parseFeeBreakdownFromNotes = (additionalNotes) => {
  if (!additionalNotes || typeof additionalNotes !== 'string') return null;
  const line = additionalNotes.split('\n')[0] || '';
  if (!line.includes('Professional fee:') || !line.includes('Subtotal:')) return null;
  const result = {
    professional_fee: 0,
    travel_logistics: 0,
    taxes: 0,
    subtotal: 0,
    totalWithFee: 0,
    currency: null,
  };
  const profMatch = line.match(/Professional fee:\s*([\d,.]+)\s*(\w+)?/i);
  if (profMatch) {
    result.professional_fee = parseFloat(String(profMatch[1]).replace(/,/g, '')) || 0;
    if (profMatch[2]) result.currency = profMatch[2].trim();
  }
  const travelMatch = line.match(/Travel\s*[&]\s*logistics:\s*([\d,.]+)/i);
  if (travelMatch) result.travel_logistics = parseFloat(String(travelMatch[1]).replace(/,/g, '')) || 0;
  const taxMatch = line.match(/Taxes:\s*([\d,.]+)/i);
  if (taxMatch) result.taxes = parseFloat(String(taxMatch[1]).replace(/,/g, '')) || 0;
  const subMatch = line.match(/Subtotal:\s*([\d,.]+)/i);
  if (subMatch) result.subtotal = parseFloat(String(subMatch[1]).replace(/,/g, '')) || 0;
  const totalMatch = line.match(/Total\s*\(incl[^)]*\):\s*([\d,.]+)/i);
  if (totalMatch) result.totalWithFee = parseFloat(String(totalMatch[1]).replace(/,/g, '')) || 0;
  return result;
};

/** Strip fee breakdown line from submitted additional_notes so we don't repeat quote amount details. */
const getAdditionalNotesWithoutFeeBreakdown = (notes) => {
  if (!notes || typeof notes !== 'string') return '';
  const lines = notes.split('\n').map((s) => s.trim()).filter(Boolean);
  const rest = lines.filter((line) => {
    const isFeeLine = line.includes('Professional fee:') && (line.includes('Subtotal:') || line.includes('Total (incl.'));
    return !isFeeLine;
  });
  return rest.join('\n').trim();
};

/** Parse additional notes (without fee line) into label-value rows for display like Quote amount. */
const parseAdditionalNotesRows = (notes) => {
  const text = getAdditionalNotesWithoutFeeBreakdown(notes);
  if (!text) return [];
  return text.split('\n').map((line) => {
    const trimmed = line.trim();
    const idx = trimmed.indexOf(': ');
    if (idx === -1) return { label: trimmed, value: '' };
    return {
      label: trimmed.substring(0, idx).trim(),
      value: trimmed.substring(idx + 2).trim(),
    };
  }).filter((row) => row.label || row.value);
};

const noteValueByLabel = (notes, labelPrefix) => {
  const rows = parseAdditionalNotesRows(notes);
  const row = rows.find((r) =>
    String(r.label || '').toLowerCase().startsWith(String(labelPrefix).toLowerCase()),
  );
  return row?.value ?? '';
};

const getFileNameFromPath = (path) => {
  if (!path || typeof path !== 'string') return path || '';
  const parts = path.replace(/\\/g, '/').split('/').filter(Boolean);
  return parts[parts.length - 1] || path;
};

const isFileValue = (value) => {
  if (!value || typeof value !== 'string') return false;
  const v = value.trim();
  return (v.includes('/') || v.includes('\\')) && /\.(pdf|jpg|jpeg|png|gif|doc|docx|xls|xlsx)$/i.test(v);
};

const getFileUrl = (path) => {
  if (!path || typeof path !== 'string') return '';
  if (path.startsWith('http')) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${getApiOrigin()}${p}`;
};

export default function QuotationFlowInspectorQuotation() {
  const { qaRequestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showFullRfq, setShowFullRfq] = useState(false);
  const [companyName, setCompanyName] = useState('QA_Quotation');
  const [existingQuotation, setExistingQuotation] = useState(null);

  const [formData, setFormData] = useState({
    professional_fee: 850,
    travel_logistics: 120,
    taxes: 0,
    currency: 'USD',
    technical_assumptions: '',
    scope_exclusions: '',
    validity_days: 30,
  });

  const [sampleReportFile, setSampleReportFile] = useState(null);
  const [certificatesFile, setCertificatesFile] = useState(null);
  const [regionNames, setRegionNames] = useState({ city: '', state: '', country: '' });
  const [categories, setCategories] = useState([]);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [qualityManagement, setQualityManagement] = useState([]);
  const sampleReportRef = useRef(null);
  const certificatesRef = useRef(null);

  const { currencyOptions } = useCurrencyMaster();

  useEffect(() => {
    const user = StorageService.getUser();
    if (user && user.company_name) setCompanyName(user.company_name);
  }, []);

  useEffect(() => {
    fetchRequest();
  }, [qaRequestId]);

  useEffect(() => {
    if (!showFullRfq) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') setShowFullRfq(false);
    };
    window.addEventListener('keydown', onEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [showFullRfq]);

  useEffect(() => {
    if (!request) {
      setRegionNames({ city: '', state: '', country: '' });
      return;
    }
    const countryId = request.inspection_location_country;
    const stateId = request.inspection_location_state;
    const cityId = request.inspection_location_city;
    const needCountry = isNumericId(countryId);
    const needState = isNumericId(stateId);
    const needCity = isNumericId(cityId);
    if (!needCountry && !needState && !needCity) {
      setRegionNames({ city: '', state: '', country: '' });
      return;
    }
    let cancelled = false;
    const run = async () => {
      let countryName = '', stateName = '', cityName = '';
      if (needCountry) {
        try {
          const res = await CommonService.get('/master/company-Country');
          const list = Array.isArray(res?.data) ? res.data : res?.data?.countries || res?.data?.master_countries || [];
          const c = list.find((x) => (x.country_id ?? x.id) == countryId);
          countryName = c?.country ?? c?.country_name ?? c?.label ?? '';
        } catch (e) {
          if (!cancelled) countryName = '';
        }
      }
      if (needState && countryId) {
        try {
          const res = await CommonService.get(`/master/company-State/${countryId}`);
          const list = Array.isArray(res?.data) ? res.data : res?.data?.states || res?.data?.master_states || [];
          const s = list.find((x) => (x.state_id ?? x.id) == stateId);
          stateName = s?.state ?? s?.state_name ?? s?.label ?? '';
        } catch (e) {
          if (!cancelled) stateName = '';
        }
      }
      if (needCity && stateId) {
        try {
          const res = await CommonService.get(`/master/company-City/${stateId}`);
          const list = Array.isArray(res?.data) ? res.data : res?.data?.cities || res?.data?.master_cities || [];
          const c = list.find((x) => (x.city_id ?? x.id) == cityId);
          cityName = c?.city ?? c?.city_name ?? c?.label ?? '';
        } catch (e) {
          if (!cancelled) cityName = '';
        }
      }
      if (!cancelled) setRegionNames({ city: cityName, state: stateName, country: countryName });
    };
    run();
    return () => { cancelled = true; };
  }, [request]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [catRes, svcRes, qmsRes] = await Promise.all([
          CommonService.get('/master/master-categories'),
          CommonService.get('/master/quality-inspector-services'),
          CommonService.get('/master/quality-management'),
        ]);
        if (cancelled) return;
        const catList = Array.isArray(catRes?.data) ? catRes.data : (catRes?.data?.categories || catRes?.data?.master_categories || []);
        const svcList = Array.isArray(svcRes?.data) ? svcRes.data : (svcRes?.data?.services || svcRes?.data?.quality_inspector_services || []);
        const qmsList = Array.isArray(qmsRes?.data) ? qmsRes.data : (qmsRes?.data?.quality_management || qmsRes?.data?.quality_management_systems || []);
        setCategories(catList);
        setInspectionTypes(svcList);
        setQualityManagement(qmsList);
      } catch (e) {
        if (!cancelled) {
          setCategories([]);
          setInspectionTypes([]);
          setQualityManagement([]);
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      setExistingQuotation(null);
      const response = await CommonService.get(API_URLS.QA.GET_BY_ID(qaRequestId));
      if (response && response.success && response.data) {
        setRequest(response.data);
        const user = StorageService.getUser();
        const userId = user?.user_login_id ?? user?.id;
        if (userId != null) {
          try {
            const quotRes = await CommonService.get(API_URLS.QA.QUOTATIONS_BY_REQUEST(qaRequestId));
            const list = quotRes?.data && Array.isArray(quotRes.data) ? quotRes.data : [];
            const mine = list.find((q) => Number(q.inspector_user_id) === Number(userId));
            if (mine) setExistingQuotation(mine);
          } catch (_) {}
        }
      } else {
        setRequest(null);
      }
    } catch (error) {
      console.error('Error fetching QA request:', error);
      toast.error('Failed to load request');
      setRequest(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!request || !currencyOptions.length || existingQuotation) return;
    const cc = request.compliance_commercials || {};
    const apiCurrency = cc.currency;
    if (apiCurrency == null || apiCurrency === '') return;
    const code = String(apiCurrency).trim();
    const match = currencyOptions.find(
      (o) => String(o.currencyCode || o.value) === code || String(o.value) === code
    );
    const valueToSet = match ? (match.currencyCode || match.value) : code;
    setFormData((prev) => {
      if (prev.currency === valueToSet) return prev;
      return { ...prev, currency: valueToSet };
    });
  }, [request, currencyOptions, existingQuotation]);

  /** When viewing a submitted quote, hydrate financials from stored breakdown (not form defaults). */
  useEffect(() => {
    if (!existingQuotation) return;
    const notes = existingQuotation.additional_notes || '';
    const breakdown = parseFeeBreakdownFromNotes(notes);
    const subtotalFromQuote = Number(existingQuotation.quote_amount);
    const validityRaw = noteValueByLabel(notes, 'Validity');
    const validityDays = parseInt(String(validityRaw).replace(/\s*days/i, '').trim(), 10);

    setFormData({
      professional_fee:
        breakdown?.professional_fee ??
        (Number.isFinite(subtotalFromQuote) ? subtotalFromQuote : 0),
      travel_logistics: breakdown?.travel_logistics ?? 0,
      taxes: breakdown?.taxes ?? 0,
      currency:
        existingQuotation.currency ||
        breakdown?.currency ||
        'USD',
      technical_assumptions: noteValueByLabel(notes, 'Technical assumptions'),
      scope_exclusions: noteValueByLabel(notes, 'Scope exclusions'),
      validity_days: Number.isFinite(validityDays) && validityDays > 0 ? validityDays : 30,
    });
  }, [existingQuotation]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const parsed = ['validity_days', 'professional_fee', 'travel_logistics', 'taxes'].includes(name)
      ? (value === '' ? '' : Number(value))
      : value;
    setFormData((prev) => ({ ...prev, [name]: parsed }));
  };

  const handleCurrencyChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, currency: value }));
  };

  const professionalFee = Number(formData.professional_fee) || 0;
  const travelLogistics = Number(formData.travel_logistics) || 0;
  const taxes = Number(formData.taxes) || 0;
  const subtotal = professionalFee + travelLogistics + taxes;
  const totalWithFee = Math.round(subtotal * (1 + FEE_PERCENT) * 100) / 100;
  const netSettlement = subtotal;

  const uploadFile = async (file, fileType = 'qa_document', category = 'company') => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('category', category);
    formDataUpload.append('companyName', companyName);
    formDataUpload.append('fileType', fileType);
    const response = await CommonService.post('/upload/single', formDataUpload);
    if (response && response.success && response.data) {
      return response.data.accessPath || response.data.filePath || '';
    }
    throw new Error(response?.message || 'Upload failed');
  };

  const handleSampleReportChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadFile(file, 'sample_report');
      setSampleReportFile(path);
    } catch (err) {
      toast.error(err?.message || 'Failed to upload sample report');
    }
  };

  const handleCertificatesChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadFile(file, 'certificates');
      setCertificatesFile(path);
    } catch (err) {
      toast.error(err?.message || 'Failed to upload certificates');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!request) return;
    if (subtotal <= 0) {
      toast.error('Please enter valid quotation amounts');
      return;
    }
    try {
      setSubmitting(true);
      const feeBreakdown = [
        `Professional fee: ${Number(formData.professional_fee) || 0} ${formData.currency || 'USD'}`,
        `Travel & logistics: ${Number(formData.travel_logistics) || 0} ${formData.currency || 'USD'}`,
        `Taxes: ${Number(formData.taxes) || 0} ${formData.currency || 'USD'}`,
        `Subtotal: ${subtotal} ${formData.currency || 'USD'}`,
        `Total (incl. ${FEE_PERCENT * 100}% fee): ${totalWithFee} ${formData.currency || 'USD'}`,
      ].join('; ');
      const additionalNotes = [
        feeBreakdown,
        formData.technical_assumptions && `Technical assumptions: ${formData.technical_assumptions}`,
        formData.scope_exclusions && `Scope exclusions: ${formData.scope_exclusions}`,
        formData.validity_days != null && formData.validity_days !== '' && `Validity: ${formData.validity_days} days`,
        sampleReportFile && `Sample report: ${sampleReportFile}`,
        certificatesFile && `Certificates: ${certificatesFile}`,
      ].filter(Boolean).join('\n');

      const payload = {
        qa_request_id: parseInt(qaRequestId, 10),
        quote_amount: subtotal,
        currency: formData.currency || 'USD',
        proposed_start_date: request.preferred_start_date || undefined,
        proposed_end_date: request.preferred_end_date || undefined,
        proposed_time: (request.preferred_time || '10:00').toString().trim().substring(0, 8) || undefined,
        estimated_duration_hours: undefined,
        inspection_approach: 'Quotation-based QA inspection; fee breakdown and documentation in additional notes.',
        additional_notes: additionalNotes || undefined,
      };

      const response = await CommonService.post(API_URLS.QA.QUOTATION_SUBMIT, payload);
      if (response && response.success) {
        const approvalMode = response?.data?.approval?.mode;
        if (approvalMode === 'pending') {
          toast.info(
            'Quotation submitted. It is pending internal service approval before the buyer can review it.',
          );
        } else {
          toast.success('Quotation submitted successfully');
        }
        navigate('/auction/qa/dashboard/requests');
      } else {
        throw new Error(response?.message || 'Failed to submit quotation');
      }
    } catch (error) {
      console.error('Error submitting quotation:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit quotation');
    } finally {
      setSubmitting(false);
    }
  };

  const rfqDisplay = useMemo(() => {
    const catOpts = categories.map((c) => ({ value: String(c.category_id ?? c.id), label: c.category ?? c.category_name ?? c.name ?? '' }));
    const typeOpts = inspectionTypes.map((s) => ({ value: String(s.quality_inspector_service_id ?? s.id), label: s.quality_inspector_service ?? s.name ?? s.label ?? '' }));
    const qmsOpts = qualityManagement.map((q) => ({ value: String(q.quality_management_id ?? q.id), label: q.quality_management ?? q.name ?? q.label ?? '' }));
    const s = request?.scope_logistics || {};
    const c = request?.compliance_commercials || {};
    return {
      inspectionCategory: resolveLabel(catOpts, s.inspection_category),
      inspectionType: resolveLabels(typeOpts, s.inspection_type),
      samplingMethod: resolveLabel(SAMPLING_METHOD_OPTIONS, s.sampling_method),
      frequency: resolveLabel(FREQUENCY_OPTIONS, s.frequency),
      budgetRange: resolveLabel(BUDGET_RANGE_OPTIONS, c.budget_range),
      reportFormat: resolveLabel(REPORT_FORMAT_OPTIONS, c.report_format),
      maxTat: resolveLabel(MAX_TAT_OPTIONS, c.max_tat),
      activeQms: resolveLabel(qmsOpts, c.active_qms),
    };
  }, [request, categories, inspectionTypes, qualityManagement]);

  if (loading) {
    return (
      <div className="quotation-flow-inspector-quotation loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="quotation-flow-inspector-quotation error">
        <p>QA request not found.</p>
        <button type="button" className="back-btn" onClick={() => navigate('/auction/qa/dashboard/requests')}>
          Back to Requests
        </button>
      </div>
    );
  }

  const scope = request.scope_logistics || {};
  const compliance = request.compliance_commercials || {};
  const productName = scope.product_name || 'Quality Inspection';
  const hasResolvedRegion = regionNames.city || regionNames.state || regionNames.country;
  const region = hasResolvedRegion
    ? [regionNames.city, regionNames.state, regionNames.country].filter(Boolean).join(', ')
    : (isNumericId(request.inspection_location_city) || isNumericId(request.inspection_location_state) || isNumericId(request.inspection_location_country))
      ? (request.inspection_location_address || '—')
      : [request.inspection_location_city, request.inspection_location_state, request.inspection_location_country].filter(Boolean).join(', ') || request.inspection_location_address || '—';
  const schedule = request.preferred_start_date
    ? `${formatDate(request.preferred_start_date)} - ${formatDate(request.preferred_end_date)}`
    : '—';
  const budget = compliance.budget_range || '—';
  const volume = scope.lot_quantity ? `${scope.lot_quantity} units` : '—';
  const currencySymbol = currencyOptions.find((o) => (o.currencyCode || o.value) === formData.currency)?.currencySymbol || '$';

  return (
    <div className="quotation-flow-inspector-quotation">
      <div className="inspector-quotation-container">
        <button type="button" className="back-link" onClick={() => navigate('/auction/qa/dashboard/requests')}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          RETURN TO MARKETPLACE
        </button>

        <h1 className="page-title">INSPECTOR QUOTATION</h1>

        <div className="inspector-quotation-layout">
          <div className="left-column">
            <div className="rfq-summary-card">
              <div className="rfq-summary-image">
                <div className="rfq-image-placeholder">QA</div>
              </div>
              <div className="rfq-summary-body">
                <h2 className="rfq-summary-title">Pre-Shipment Inspection: {productName}</h2>
                <span className="rfq-id-pill">{request.qa_request_number || `QA-${request.qa_request_id}`}</span>
                <div className="rfq-summary-details">
                  <div className="rfq-detail-row">
                    <span className="rfq-detail-label">REGION:</span>
                    <span className="rfq-detail-value">{region}</span>
                  </div>
                  <div className="rfq-detail-row">
                    <span className="rfq-detail-label">VOLUME:</span>
                    <span className="rfq-detail-value">{volume}</span>
                  </div>
                  <div className="rfq-detail-row">
                    <span className="rfq-detail-label">SCHEDULE:</span>
                    <span className="rfq-detail-value">{schedule}</span>
                  </div>
                  <div className="rfq-detail-row">
                    <span className="rfq-detail-label">BUDGET:</span>
                    <span className="rfq-detail-value">{budget}</span>
                  </div>
                </div>
              </div>
            </div>

            <section className="section quotation-financials">
              <h3 className="section-title">QUOTATION FINANCIALS</h3>
              <p className="section-subtitle">
                {existingQuotation
                  ? 'Submitted quote breakdown (read-only)'
                  : 'Total transparency for buyer settlement'}
              </p>
              <div className="financials-rows">
                <div className="financials-row">
                  <div className="financial-field">
                    <label>PROFESSIONAL FEE *</label>
                    <input
                      type="number"
                      name="professional_fee"
                      value={formData.professional_fee}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      readOnly={!!existingQuotation}
                      disabled={!!existingQuotation}
                    />
                  </div>
                  <div className="financial-field">
                    <label>TRAVEL & LOGISTICS</label>
                    <input
                      type="number"
                      name="travel_logistics"
                      value={formData.travel_logistics}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      readOnly={!!existingQuotation}
                      disabled={!!existingQuotation}
                    />
                  </div>
                </div>
                <div className="financials-row">
                  <div className="financial-field">
                    <label>TAXES</label>
                    <input
                      type="number"
                      name="taxes"
                      value={formData.taxes}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      readOnly={!!existingQuotation}
                      disabled={!!existingQuotation}
                    />
                  </div>
                  <div className="financial-field">
                    <label>CURRENCY</label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleCurrencyChange}
                      disabled={!!existingQuotation}
                    >
                      {currencyOptions.map((opt) => (
                        <option key={opt.value} value={opt.currencyCode || opt.value}>
                          {opt.currencyCode || opt.value} ({opt.currencySymbol || ''})
                        </option>
                      ))}
                      {(!currencyOptions || currencyOptions.length === 0) && (
                        <option value="USD">USD ($)</option>
                      )}
                    </select>
                  </div>
                </div>
              </div>
              <div className="totals-card">
                <div className="total-block">
                  <div className="total-label">TOTAL QUOTE (INCL. 25% FEE)</div>
                  <div className="total-value">{currencySymbol}{totalWithFee.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="total-block net">
                  <div className="total-label">YOUR NET SETTLEMENT</div>
                  <div className="total-value">{currencySymbol}{netSettlement.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                </div>
              </div>
            </section>
          </div>

          <div className="right-column">
            <div className="action-buttons-top">
              <button type="button" className="btn-view-rfq" onClick={() => setShowFullRfq(true)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h18M9 21V9" />
                </svg>
                VIEW FULL RFQ DETAILS
              </button>
            </div>

            {showFullRfq && (
              <div className="rfq-details-modal-overlay" onClick={() => setShowFullRfq(false)} role="presentation">
                <div className="rfq-details-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="rfq-details-modal-title">
                  <div className="rfq-details-modal-header">
                    <div className="rfq-details-modal-header-left">
                      <div className="rfq-details-modal-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                      </div>
                      <div>
                        <h2 id="rfq-details-modal-title" className="rfq-details-modal-title">Full RFQ Details</h2>
                        <p className="rfq-details-modal-subtitle">Complete scope, location, schedule and compliance information</p>
                      </div>
                    </div>
                    <button type="button" className="rfq-details-modal-close" onClick={() => setShowFullRfq(false)} aria-label="Close">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                  <div className="rfq-details-modal-body">
                    <section className="rfq-detail-section">
                      <h4>Inspection & Product</h4>
                      <dl className="rfq-detail-dl">
                        {scope.product_name && <><dt>Product name</dt><dd>{scope.product_name}</dd></>}
                        {(rfqDisplay.inspectionCategory || scope.inspection_category) && <><dt>Inspection category</dt><dd>{rfqDisplay.inspectionCategory || scope.inspection_category}</dd></>}
                        {(rfqDisplay.inspectionType || scope.inspection_type) && <><dt>Inspection type</dt><dd>{rfqDisplay.inspectionType || (Array.isArray(scope.inspection_type) ? scope.inspection_type.join(', ') : scope.inspection_type)}</dd></>}
                        {(rfqDisplay.samplingMethod || scope.sampling_method) && <><dt>Sampling method</dt><dd>{rfqDisplay.samplingMethod || scope.sampling_method}</dd></>}
                        {scope.lot_quantity && <><dt>Lot quantity</dt><dd>{scope.lot_quantity}</dd></>}
                        {scope.acceptance_criteria && <><dt>Acceptance criteria</dt><dd>{scope.acceptance_criteria}</dd></>}
                        {(rfqDisplay.frequency || scope.frequency) && <><dt>Frequency</dt><dd>{rfqDisplay.frequency || scope.frequency}</dd></>}
                        {scope.industry_standards && <><dt>Industry standards</dt><dd>{scope.industry_standards}</dd></>}
                      </dl>
                    </section>
                    <section className="rfq-detail-section">
                      <h4>Location & Contact</h4>
                      <dl className="rfq-detail-dl">
                        {(request.inspection_location_address || scope.address_line_1) && <><dt>Address</dt><dd>{request.inspection_location_address || scope.address_line_1}</dd></>}
                        {(scope.address_line_2 || request.inspection_location_address_line_2) && <><dt>Address line 2</dt><dd>{scope.address_line_2 || request.inspection_location_address_line_2}</dd></>}
                        {region !== '—' && <><dt>Region</dt><dd>{region}</dd></>}
                        {request.inspection_location_pincode && <><dt>Pincode</dt><dd>{request.inspection_location_pincode}</dd></>}
                        {scope.contact_name && <><dt>Contact name</dt><dd>{scope.contact_name}</dd></>}
                        {scope.designation && <><dt>Designation</dt><dd>{scope.designation}</dd></>}
                        {scope.email && <><dt>Email</dt><dd>{scope.email}</dd></>}
                        {scope.phone && <><dt>Phone</dt><dd>{scope.phone}</dd></>}
                      </dl>
                    </section>
                    <section className="rfq-detail-section">
                      <h4>Schedule & Deadlines</h4>
                      <dl className="rfq-detail-dl">
                        {request.preferred_start_date && <><dt>Preferred start date</dt><dd>{formatDate(request.preferred_start_date)}</dd></>}
                        {request.preferred_end_date && <><dt>Preferred end date</dt><dd>{formatDate(request.preferred_end_date)}</dd></>}
                        {request.preferred_time && <><dt>Preferred time</dt><dd>{request.preferred_time}</dd></>}
                        {request.bid_deadline && <><dt>Bid deadline</dt><dd>{formatDate(request.bid_deadline)}</dd></>}
                        {scope.flexible_dates != null && <><dt>Flexible dates</dt><dd>{scope.flexible_dates ? 'Yes' : 'No'}</dd></>}
                        {scope.flexible_days != null && <><dt>Flexible days</dt><dd>{scope.flexible_days}</dd></>}
                      </dl>
                    </section>
                    <section className="rfq-detail-section">
                      <h4>Compliance & Commercials</h4>
                      <dl className="rfq-detail-dl">
                        {(rfqDisplay.budgetRange || compliance.budget_range) && <><dt>Budget range</dt><dd>{rfqDisplay.budgetRange || compliance.budget_range}</dd></>}
                        {compliance.currency && <><dt>Currency</dt><dd>{compliance.currency}</dd></>}
                        {(rfqDisplay.reportFormat || compliance.report_format) && <><dt>Report format</dt><dd>{rfqDisplay.reportFormat || compliance.report_format}</dd></>}
                        {(rfqDisplay.maxTat || compliance.max_tat) && <><dt>Max TAT</dt><dd>{rfqDisplay.maxTat || compliance.max_tat}</dd></>}
                        {(rfqDisplay.activeQms || compliance.active_qms) && <><dt>Active QMS</dt><dd>{rfqDisplay.activeQms || compliance.active_qms}</dd></>}
                        {compliance.system_comments && <><dt>System comments</dt><dd>{compliance.system_comments}</dd></>}
                        {compliance.compliance_history && <><dt>Compliance history</dt><dd>{compliance.compliance_history}</dd></>}
                        {compliance.reporting_notes && <><dt>Reporting notes</dt><dd>{compliance.reporting_notes}</dd></>}
                      </dl>
                    </section>
                    {(scope.detailed_inspection_instructions || scope.security_ppe_requirements || compliance.risk_description || compliance.contingency_mitigation) && (
                      <section className="rfq-detail-section">
                        <h4>Instructions & Notes</h4>
                        <dl className="rfq-detail-dl">
                          {scope.detailed_inspection_instructions && <><dt>Detailed inspection instructions</dt><dd>{scope.detailed_inspection_instructions}</dd></>}
                          {scope.security_ppe_requirements && <><dt>Security / PPE requirements</dt><dd>{scope.security_ppe_requirements}</dd></>}
                          {compliance.risk_description && <><dt>Risk description</dt><dd>{compliance.risk_description}</dd></>}
                          {compliance.contingency_mitigation && <><dt>Contingency & mitigation</dt><dd>{compliance.contingency_mitigation}</dd></>}
                        </dl>
                      </section>
                    )}
                  </div>
                  <div className="rfq-details-modal-footer">
                    <button type="button" className="rfq-details-modal-btn-close" onClick={() => setShowFullRfq(false)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {existingQuotation ? (
              <div className="submitted-quotation-view">
                <h3 className="section-title">SUBMITTED QUOTATION</h3>
                <div className="submitted-quotation-card">
                  <div className="submitted-quotation-row">
                    <span className="submitted-quotation-label">Quote amount</span>
                    <span className="submitted-quotation-value">
                      {existingQuotation.currency || 'USD'} {Number(existingQuotation.quote_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="submitted-quotation-row">
                    <span className="submitted-quotation-label">Status</span>
                    <span className={`submitted-quotation-status submitted-quotation-status-${(existingQuotation.status || '').toLowerCase()}`}>
                      {existingQuotation.status === 'pending_approval' ||
                      existingQuotation.quote_approval_status === 'pending'
                        ? 'Pending service approval'
                        : existingQuotation.status || 'submitted'}
                    </span>
                  </div>
                  {(existingQuotation.status === 'pending_approval' ||
                    existingQuotation.quote_approval_status === 'pending') && (
                    <p className="submitted-quotation-approval-note">
                      This quote is awaiting internal service approval before the buyer can review it on their QA
                      quotations list.
                    </p>
                  )}
                  {existingQuotation.submitted_at && (
                    <div className="submitted-quotation-row">
                      <span className="submitted-quotation-label">Submitted on</span>
                      <span className="submitted-quotation-value">{formatDate(existingQuotation.submitted_at)}</span>
                    </div>
                  )}
                  {parseAdditionalNotesRows(existingQuotation.additional_notes).map((row, idx) => {
                    const isFile = isFileValue(row.value);
                    const fileUrl = isFile ? getFileUrl(row.value) : '';
                    const fileName = isFile ? getFileNameFromPath(row.value) : row.value;
                    return (
                      <div key={idx} className="submitted-quotation-row submitted-quotation-row-file">
                        <span className="submitted-quotation-label">{row.label}</span>
                        <div className="submitted-quotation-value-wrap">
                          <span className="submitted-quotation-value">{fileName}</span>
                          {isFile && fileUrl && (
                            <span className="submitted-quotation-file-actions">
                              <button
                                type="button"
                                className="submitted-quotation-btn submitted-quotation-btn-view"
                                onClick={() => window.open(fileUrl, '_blank')}
                              >
                                View
                              </button>
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={getFileNameFromPath(row.value)}
                                className="submitted-quotation-btn submitted-quotation-btn-download"
                              >
                                Download
                              </a>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="quotation-form">
                <section className="section notes-terms">
                  <h3 className="section-title">NOTES & TERMS</h3>
                  <div className="form-group">
                    <label>TECHNICAL ASSUMPTIONS</label>
                    <textarea
                      name="technical_assumptions"
                      value={formData.technical_assumptions}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Describe technical assumptions..."
                    />
                  </div>
                  <div className="form-group">
                    <label>SCOPE EXCLUSIONS</label>
                    <textarea
                      name="scope_exclusions"
                      value={formData.scope_exclusions}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Describe scope exclusions..."
                    />
                  </div>
                  <div className="form-group">
                    <label>VALIDITY (DAYS)</label>
                    <input
                      type="number"
                      name="validity_days"
                      value={formData.validity_days}
                      onChange={handleInputChange}
                      min="1"
                    />
                  </div>
                </section>

                <section className="section doc-section doc-section-sample-report">
                  <h3 className="section-title">SAMPLE REPORT</h3>
                  <div
                    className="upload-box"
                    onClick={() => sampleReportRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && sampleReportRef.current?.click()}
                  >
                    <input
                      ref={sampleReportRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleSampleReportChange}
                      style={{ display: 'none' }}
                    />
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span className="upload-text">UPLOAD SAMPLE REPORT</span>
                    <span className="upload-hint">PDF/JPG/PNG (max 10MB)</span>
                    {sampleReportFile && <span className="uploaded-name">✓ Sample report uploaded</span>}
                  </div>
                </section>

                <section className="section doc-section doc-section-certificates">
                  <h3 className="section-title">CERTIFICATES</h3>
                  <div
                    className="upload-box"
                    onClick={() => certificatesRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && certificatesRef.current?.click()}
                  >
                    <input
                      ref={certificatesRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleCertificatesChange}
                      style={{ display: 'none' }}
                    />
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                    <span className="upload-text">UPLOAD CERTIFICATES</span>
                    <span className="upload-hint">PDF/JPG/PNG (max 10MB)</span>
                    {certificatesFile && <span className="uploaded-name">✓ Certificates uploaded</span>}
                  </div>
                </section>

                <button type="submit" className="btn-submit-quote" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'SUBMIT QUOTE'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
